import AudioAndVideoPlayer from '@/components/AudioAndVideoPlayer';
import CommentsSection from '@/components/CommentsSection/Index';
import GoBackMenu from '@/components/GoBackNavbar';
import HtmlPlayer from '@/components/HtmlPlayer';
import SideMenu from '@/components/SideMenu';
import Constants from '@/Constants';
import { useMetadata } from '@/hooks/useMetadata';
import { AvailableTranslation } from '@/models/AvailableTranslation';
import { ContentMetadata } from '@/models/ContentMetadata';
import { EntityType } from '@/models/EntityTypes';
import { LanguageType } from '@/models/LanguageType';
import { Lesson } from '@/models/Lesson';
import { LessonContentTO } from '@/models/LessonContentTO';
import { LessonData } from '@/models/LessonData';
import { analyticsService, PlaylistEvents } from '@/services/AnalyticsService';
import { contentService } from '@/services/ContentService';
import { ApplicationState } from '@/store';
import { hideHeaderMenu, showHeaderMenu } from '@/store/header/action';
import { clearContent } from '@/store/item/action';
import { coursesHavingAContentClear } from '@/store/list/action';
import { clearCurrentContentLanguage, setCurrentContentLanguage } from '@/store/locale/action';
import { searchClear } from '@/store/search/action';
import { addContentToCourseRequest, deleteContentFromCourseRequest, getAddedContentListRequest } from '@/store/student/actions';
import { History } from 'history';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Attachments from '../Attachments';
import Menu from '../ContentMoreOptions/Menu';
import CourseSection from '../CourseSection';
import ExternalPlayer from '../ExternalPlayer';
import HorizontalMenu from '../HorizontalMenu';
import './style.scss';

interface LocationState {
    fromContentPreview: boolean;
}

const ContentScreen: React.FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { courseId } = useParams<{ courseId: string }>();

    const { content, currentContentLanguage, strings, addedLessons, personId, addOrRemLoading, listLocale } = useSelector(
        (state: ApplicationState) => ({
            content: state.itens.currentData.content,
            currentContentLanguage: state.locale.currentContentLanguage || (state.locale.data._LANGUAGE as LanguageType),
            strings: state.locale.data,
            addedLessons: state.student.myListData.courseStudentView.course.lessons || [],
            listLocale: state.student.myListData.courseStudentView.course.defaultLocale,
            personId: state.identity.data.person.id || 0,
            addOrRemLoading: state.student.myListData.loading,
        }),
    );

    const [metaData, setMetaData] = useState<ContentMetadata>({} as ContentMetadata);
    const [contentTranslation, setContentTranslation] = useState<AvailableTranslation>();
    const [mustShowDescription, setMustShowDescription] = useState<boolean>(true);
    const [showAddContent, setShowAddContent] = useState<boolean>(true);
    const [goBackToSuggestedPath, setGoBackToSuggestedPath] = useState<History.LocationDescriptor<LocationState>>();
    const goBackToSuggestedRef = useRef<boolean>(false);

    const contentPathToMetadata = useCallback((): string | undefined => {
        if (content && content.mediaType === 'EXTERNAL' && currentContentLanguage && currentContentLanguage.length > 0) {
            return contentService.getContentPathByLanguageType(content, currentContentLanguage);
        }

        return undefined;
    }, [content, currentContentLanguage]);

    const { metadata } = useMetadata(contentPathToMetadata());

    const addContentToCourse = (): void => {
        const lessonData: LessonData[] = [
            {
                mediaLesson: ({
                    '@class': '.LessonContentTO',
                    id: content.id,
                    course: { id: courseId },
                    locale: listLocale,
                } as unknown) as LessonContentTO,
            },
        ];
        dispatch(addContentToCourseRequest(lessonData, personId, Number(courseId)));
        analyticsService.logEvent(PlaylistEvents.ADD_ITEM_ON_STUDENT_PLAYLIST, {
            entityId: content.id,
            entityName: content.name,
            entityType: Constants.entityTypes.CONTENT,
        });
    };

    const removeLessonFromCourse = () => {
        const lessonId = addedLessons.find((l: Lesson) => l.content.id === content.id)?.id;
        lessonId && dispatch(deleteContentFromCourseRequest(lessonId, personId, Number(courseId)));
    };

    useEffect(() => {
        if (history.location.pathname.indexOf('/mine/') > -1) {
            dispatch(getAddedContentListRequest(personId, Number(courseId), true));
            dispatch(hideHeaderMenu());
            dispatch(setCurrentContentLanguage(listLocale as LanguageType));
            setGoBackToSuggestedPath({ pathname: `/mine/courses/${courseId}/suggestedcontents`, state: { fromContentPreview: true } });
        }

        return () => {
            dispatch(clearContent());
            dispatch(clearCurrentContentLanguage());
            dispatch(coursesHavingAContentClear());
            dispatch(showHeaderMenu());

            if (!goBackToSuggestedRef.current && !history.location.pathname.includes(Constants.SEARCH_RESULT_URL)) {
                dispatch(searchClear());
            }
        };
    }, []);

    useEffect(() => {
        if (goBackToSuggestedPath) {
            setShowAddContent(addedLessons.findIndex((l: Lesson) => l.content.id === content.id) === -1);
        }
    }, [addedLessons]);

    useEffect(() => {
        if (content) {
            setMetaData(contentService.getContentMetadata(content));
            setContentTranslation(contentService.getContentTranslationByLanguageType(content, currentContentLanguage));

            if (content.mediaType === 'HTML' || content.mediaType === 'EXTERNAL') {
                setMustShowDescription(false);
            }
        }
    }, [content]);

    useEffect(() => {
        if (content) {
            setContentTranslation(contentService.getContentTranslationByLanguageType(content, currentContentLanguage));
        }
    }, [currentContentLanguage]);

    const getPlayerByType = (): React.ReactNode => {
        switch (content.mediaType) {
            case 'AUDIO':
            case 'VIDEO':
                return <AudioAndVideoPlayer content={content} />;
            case 'HTML':
                return <HtmlPlayer content={content} />;
            case 'EXTERNAL':
                return <ExternalPlayer content={content} metadata={metaData} />;
            default:
                return null;
        }
    };

    const getButtonLabel = (btnLabel: string) => {
        return addOrRemLoading ? <Spinner animation="border" size="sm" /> : btnLabel;
    };

    const onGoBackMenuClickHandler = () => {
        if (goBackToSuggestedPath) {
            goBackToSuggestedRef.current = true;
        }
    };

    const onGoBackButtonClickHandler = () => {
        if (goBackToSuggestedPath) {
            goBackToSuggestedRef.current = true;
            history.replace(goBackToSuggestedPath);
        } else {
            history.goBack();
        }
    };

    return (
        <>
            {content && contentTranslation && (
                <div className="content-page">
                    <GoBackMenu goBackPath={goBackToSuggestedPath} onClick={onGoBackMenuClickHandler} replaceHistory={!!goBackToSuggestedPath} />
                    <div className="content-main">
                        <div className="item-bg" id={Constants.TOP_IMG_ID}>
                            <img className="item-bg__img" src={content.mediaType === 'EXTERNAL' ? metadata?.image : content.mainImage} />
                        </div>
                        <div className="container">
                            <div className="row">
                                <div className="col-sm-8 offset-sm-2">
                                    <div className="item-content">
                                        <h1 className="item-content__title">{contentTranslation.name}</h1>
                                        <div className="item-content__info">
                                            <div className="item-content__author">{contentTranslation.author}</div>
                                            <div className="item-content__duration">{`${contentTranslation.workload} ${strings.MINUTES}`}</div>
                                        </div>
                                        <HorizontalMenu
                                            entity={{ id: content.id as number, name: content.name, type: EntityType.CONTENT }}
                                            hasAttachments
                                            hasComments
                                            hasFavorite
                                            moduleId={Constants.PLAYLIST_MODULE_ID}
                                        />
                                    </div>
                                    <div className="item-content__stage">{getPlayerByType()}</div>
                                    {mustShowDescription && <div className="item-content__description">{contentTranslation.description}</div>}
                                    {!goBackToSuggestedPath && (
                                        <>
                                            <Attachments content={content} locale={content?.locale} />
                                            <CommentsSection entity={content} type={EntityType.CONTENT} />
                                        </>
                                    )}
                                </div>
                                {goBackToSuggestedPath ? (
                                    <div className="add-content-bar">
                                        <Button className="btn-outlined" onClick={onGoBackButtonClickHandler}>
                                            {strings.GO_BACK}
                                        </Button>
                                        {showAddContent ? (
                                            <Button disabled={addOrRemLoading} onClick={addContentToCourse}>
                                                {getButtonLabel(strings.ADD)}
                                            </Button>
                                        ) : (
                                            <Button className="btn-remove" disabled={addOrRemLoading} onClick={removeLessonFromCourse}>
                                                {!addOrRemLoading && <i className="material-icons-outlined">{Constants.icons.DELETE}</i>}
                                                {getButtonLabel(strings.REMOVE)}
                                            </Button>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="col-sm-8 offset-sm-2 course-having-content">
                                            <CourseSection
                                                mode={'view'}
                                                slideConfiguration={{ slidesToShow: 2.5, slidesToScroll: 2 }}
                                                viewType="listCoursesHavingAContentStudentView"
                                            />
                                        </div>
                                        <SideMenu conclude entityType={EntityType.CONTENT} moreOptions={<Menu />} rating />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
export default ContentScreen;
